// per docs/shape-uc-31-system-credentials.md §Addendum 2026-08-28 (credential lifecycle)
import { apiClient } from '../../app/api-client';
import { generateClientSecret } from './web-crypto';
import type { AddressFormat } from './ip-address';

export interface SystemCredential {
  id: string;
  clientId: string;
  registrarId: string;
  allowedIps: string[];
  allowedIpCidrs: string[];
  // CF-48: an address carries the credential's two blocks, the System
  // Administrator's (UC21-08) and the Registrar Administrator's (UC31-06).
  suspendedIps: string[];
  registrarSuspendedIps: string[];
  // CF-48: `active` is "neither block set"; the two say who blocks.
  active: boolean;
  blockedBySystemAdmin: boolean;
  blockedByRegistrar: boolean;
  createdAtIso: string;
  updatedAtIso: string | null;
  // UC31-02/03: when the current secret was set (issued or rotated); null only
  // from a server that predates it. `updatedAtIso` moves with every IP edit.
  secretSetAtIso: string | null;
  lastUsedAtIso: string | null;
  // caiet UC21-10 — who made the change `updatedAtIso` stamps. Empty where the
  // change predates the credential trail; the register renders that as "not
  // recorded" rather than as the system.
  lastChangeActorId: string;
  lastChangeActorRole: string;
}

// A `Blockable` (features/blocking): `active` is "neither level suspends it",
// and the two flags say who does (CF-48).
export interface AdmittedAddress {
  address: string;
  format: AddressFormat;
  active: boolean;
  blockedBySystemAdmin: boolean;
  blockedByRegistrar: boolean;
}

interface CredentialEnvelope {
  credential: SystemCredential;
}

export interface CredentialScope {
  clientId: string;
  registrarId: string;
}

// One address per row for the interface, out of the two columns the server keeps
// (plain addresses and CIDR ranges) plus the two suspension lists: the
// Administrator Sistemului's (caiet UC21-08) and the Administrator
// Registratorului's (UC31-06), independent levels under CF-48.
export function admittedAddresses(credential: SystemCredential): AdmittedAddress[] {
  const bySystemAdmin = new Set(credential.suspendedIps);
  const byRegistrar = new Set(credential.registrarSuspendedIps ?? []);
  return [...credential.allowedIps, ...credential.allowedIpCidrs].map((address) => ({
    address,
    format: address.includes(':') ? 'IPv6' : 'IPv4',
    active: !bySystemAdmin.has(address) && !byRegistrar.has(address),
    blockedBySystemAdmin: bySystemAdmin.has(address),
    blockedByRegistrar: byRegistrar.has(address),
  }));
}

// caiet UC31-05: one field takes both families, with or without a CIDR prefix,
// so the Administrator types what its firewall rule says instead of choosing a
// column first. The server keeps ranges apart from single addresses.
export function splitAllowlist(addresses: string[]): {
  allowedIps: string[];
  allowedIpCidrs: string[];
} {
  return {
    allowedIps: addresses.filter((address) => !address.includes('/')),
    allowedIpCidrs: addresses.filter((address) => address.includes('/')),
  };
}

function scopeParams(registrarId: string): { params?: { registrarId: string } } {
  return registrarId ? { params: { registrarId } } : {};
}

export async function listCredentials(registrarId?: string): Promise<SystemCredential[]> {
  const { data } = await apiClient.get<{ credentials: SystemCredential[] }>(
    '/admin/credentials',
    scopeParams(registrarId ?? ''),
  );
  return data.credentials ?? [];
}

// caiet UC31-02: the secret is generated here, in the browser, and only its
// SHA-256 crosses the wire. The plaintext this returns came from this device and
// the server has no way to produce it again.
export async function createCredential(input: {
  registrarId: string;
  addresses: string[];
}): Promise<{ credential: SystemCredential; clientSecret: string }> {
  const { plaintext, sha256Hex } = await generateClientSecret();
  const { data } = await apiClient.post<CredentialEnvelope>(
    '/admin/credentials',
    { secretSha256Hex: sha256Hex, ...splitAllowlist(input.addresses) },
    scopeParams(input.registrarId),
  );
  return { credential: data.credential, clientSecret: plaintext };
}

// caiet UC31-01 reset. Same zero-knowledge exchange as the first set, against a
// credential that keeps its client_id, its allowlist and its active flag.
export async function rotateCredentialSecret(
  scope: CredentialScope,
): Promise<{ credential: SystemCredential; clientSecret: string }> {
  const { plaintext, sha256Hex } = await generateClientSecret();
  const { data } = await apiClient.patch<CredentialEnvelope>(
    `/admin/credentials/${encodeURIComponent(scope.clientId)}/secret`,
    { secretSha256Hex: sha256Hex },
    scopeParams(scope.registrarId),
  );
  return { credential: data.credential, clientSecret: plaintext };
}

export async function setCredentialActive(
  scope: CredentialScope & { active: boolean },
): Promise<SystemCredential> {
  const { data } = await apiClient.patch<CredentialEnvelope>(
    `/admin/credentials/${encodeURIComponent(scope.clientId)}/active`,
    { active: scope.active },
    scopeParams(scope.registrarId),
  );
  return data.credential;
}

export async function setCredentialAllowlist(
  scope: CredentialScope & { addresses: string[] },
): Promise<SystemCredential> {
  const { data } = await apiClient.patch<CredentialEnvelope>(
    `/admin/credentials/${encodeURIComponent(scope.clientId)}/allowlist`,
    splitAllowlist(scope.addresses),
    scopeParams(scope.registrarId),
  );
  return data.credential;
}

export async function setAddressActive(
  scope: CredentialScope & { address: string; active: boolean },
): Promise<SystemCredential> {
  const { data } = await apiClient.patch<CredentialEnvelope>(
    `/admin/credentials/${encodeURIComponent(scope.clientId)}/ip-state`,
    { address: scope.address, active: scope.active },
    scopeParams(scope.registrarId),
  );
  return data.credential;
}
